<template>
  <div style="width: 100%">
    <v-tooltip
      :right="block"
      :top="!block"
      max-width="340"
      content-class="preview-tooltip"
    >
      <template #activator="{ on, attrs }">
        <v-btn
          depressed
          color="primary"
          class="multi-line-button"
          :block="block"
          v-bind="attrs"
          v-on="on"
          @click="preview = true"
        >
          <v-spacer />
          {{ $t('article.preview') }}
          <v-spacer />
          <v-icon class="ml-2">
            open_in_new
          </v-icon>
        </v-btn>
      </template>
      <span>{{ $t('article.previewTooltip') }}</span>
    </v-tooltip>
    <v-dialog
      v-model="preview"
      attach="#app"
      :fullscreen="expanded"
      :max-width="expanded ? '100%' : '1440px'"
      :content-class="dialogClass"
    >
      <v-card class="preview-shell" :class="{ 'preview-shell--expanded': expanded }">
        <header class="preview-header">
          <div class="d-flex align-start">
            <v-avatar color="primary" size="40" class="mr-3 flex-shrink-0">
              <v-icon color="white">menu_book</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-center">
                <h2 class="text-h6 font-weight-bold mb-0 mr-3">
                  {{ $t('article.previewDialog.title') }}
                </h2>
                <v-chip x-small class="text-none font-weight-bold" color="primary" outlined>
                  {{ $t('article.previewDialog.badge') }}
                </v-chip>
              </div>
              <p class="mb-0 mt-1 caption text--secondary">
                {{ $t('article.previewDialog.subtitle') }}
              </p>
            </div>
            <v-btn icon :title="$t('article.previewDialog.expand')" @click="expanded = !expanded">
              <v-icon>{{ expanded ? 'fullscreen_exit' : 'fullscreen' }}</v-icon>
            </v-btn>
            <v-btn icon :title="$t('close')" @click="preview = false">
              <v-icon>close</v-icon>
            </v-btn>
          </div>
        </header>

        <div class="preview-toolbar">
          <div class="d-flex flex-wrap align-center">
            <button
              v-for="mode in viewModes"
              :key="mode.id"
              type="button"
              class="preview-seg"
              :class="{ 'preview-seg--active': viewMode === mode.id }"
              @click="viewMode = mode.id"
            >
              <v-icon x-small class="mr-1">{{ mode.icon }}</v-icon>
              {{ $t(mode.label) }}
            </button>
          </div>
          <div class="d-flex flex-wrap align-center preview-toolbar-right">
            <template v-if="viewMode !== 'code'">
              <button
                v-for="device in devices"
                :key="device.id"
                type="button"
                class="preview-seg preview-seg--quiet"
                :class="{ 'preview-seg--active': deviceMode === device.id }"
                @click="deviceMode = device.id"
              >
                <v-icon x-small class="mr-1">{{ device.icon }}</v-icon>
                {{ $t(device.label) }}
              </button>
              <button
                type="button"
                class="preview-seg preview-seg--quiet"
                :class="{ 'preview-seg--active': fontFamily === 'sans' }"
                @click="fontFamily = 'sans'"
              >Sans</button>
              <button
                type="button"
                class="preview-seg preview-seg--quiet"
                :class="{ 'preview-seg--active': fontFamily === 'serif' }"
                @click="fontFamily = 'serif'"
              >Serif</button>
              <div class="preview-font-size ml-1">
                <button type="button" class="preview-seg preview-seg--quiet" @click="bumpFont(-1)">A-</button>
                <button type="button" class="preview-seg preview-seg--active">A</button>
                <button type="button" class="preview-seg preview-seg--quiet" @click="bumpFont(1)">A+</button>
              </div>
            </template>
            <button type="button" class="preview-seg" @click="copyFullHtml">
              <v-icon x-small class="mr-1">code</v-icon>
              {{ $t('article.previewDialog.copyHtml') }}
            </button>
            <button type="button" class="preview-seg" @click="copyMarkdown">
              <v-icon x-small class="mr-1">description</v-icon>
              {{ $t('article.previewDialog.markdown') }}
            </button>
            <button type="button" class="preview-seg" @click="printPreview">
              <v-icon x-small class="mr-1">print</v-icon>
              {{ $t('article.previewDialog.print') }}
            </button>
          </div>
        </div>

        <div class="preview-body" :class="'preview-body--' + viewMode">
          <div
            v-show="viewMode !== 'code'"
            ref="readerScroll"
            class="preview-reader-scroll"
          >
            <article
              class="preview-paper"
              :class="[
                'preview-paper--' + deviceMode,
                'preview-paper--' + fontFamily,
                { 'preview-paper--full': expanded && deviceMode === 'desktop' }
              ]"
              :style="{ fontSize: (16 * fontScale) + 'px' }"
            >
              <h1>{{ bundle.readerH1 || $t('article.previewDialog.untitled') }}</h1>
              <p
                v-if="bundle.metaDescription"
                class="preview-meta-description text--secondary"
              >
                {{ bundle.metaDescription }}
              </p>
              <div class="preview-meta">
                <span><v-icon x-small>event</v-icon> {{ $t('article.previewDialog.updatedNow') }}</span>
                <span>·</span>
                <span><v-icon x-small>schedule</v-icon> {{ $t('article.previewDialog.readTime', [readMinutes]) }}</span>
                <span>·</span>
                <span><v-icon x-small>description</v-icon> {{ $t('article.previewDialog.words', [bundle.wordCount]) }}</span>
              </div>
              <nav v-if="bundle.headings.length" class="preview-toc">
                <div class="preview-toc-title"># {{ $t('article.contentList.title') }}</div>
                <ol>
                  <li v-for="(heading, index) in bundle.headings" :key="heading.id">
                    <a href="#" @click.prevent="scrollToHeading(heading.id)">
                      <span class="preview-toc-num">{{ index + 1 }}.</span>
                      {{ heading.text }}
                    </a>
                  </li>
                </ol>
              </nav>
              <div
                class="preview-article-html"
                v-html="sanitizedBody"
              />
              <div v-if="previewImages.length" class="preview-images">
                <figure v-for="(image, index) in previewImages" :key="image.url + index">
                  <img :src="image.url" :alt="plainAlt(image.alt)">
                  <figcaption v-if="plainAlt(image.alt)">{{ plainAlt(image.alt) }}</figcaption>
                </figure>
              </div>
            </article>
          </div>

          <div v-show="viewMode !== 'reader'" class="preview-code-pane">
            <div class="preview-code-tabs">
              <button
                v-for="tab in codeTabs"
                :key="tab.id"
                type="button"
                class="preview-seg"
                :class="{ 'preview-seg--active': codeTab === tab.id }"
                @click="codeTab = tab.id"
              >
                <v-icon x-small class="mr-1">{{ tab.icon }}</v-icon>
                {{ $t(tab.label) }}
              </button>
            </div>
            <div class="d-flex flex-wrap align-center px-3 py-2">
              <v-btn x-small depressed class="text-none mr-2 mb-1" @click="downloadCurrent">
                <v-icon x-small left>download</v-icon>
                {{ $t('article.previewDialog.download') }}
              </v-btn>
              <v-btn x-small depressed color="primary" class="text-none mr-2 mb-1" @click="copyCurrentCode">
                <v-icon x-small left>content_copy</v-icon>
                {{ $t('article.previewDialog.copyCode') }}
              </v-btn>
              <v-btn
                x-small
                depressed
                color="primary"
                class="text-none mr-2 mb-1"
                href="https://validator.schema.org/"
                target="_blank"
                rel="noopener noreferrer"
                @click="copySchemaForValidator"
              >
                {{ $t('article.previewDialog.schemaValidator') }}
                <v-icon x-small right>open_in_new</v-icon>
              </v-btn>
              <v-spacer />
              <v-text-field
                v-if="codeTab !== 'audit'"
                v-model="codeSearch"
                dense
                hide-details
                outlined
                class="preview-search mb-1"
                :placeholder="$t('article.previewDialog.searchCode')"
                prepend-inner-icon="search"
              />
            </div>
            <div v-if="codeTab !== 'audit'" class="preview-code-meta px-3 caption">
              {{ $t('article.previewDialog.lines', [codeLines.length]) }}
              &nbsp;·&nbsp;
              {{ $t('article.previewDialog.bytes', [codeBytes]) }}
              &nbsp;·&nbsp;
              <span class="purple--text text--lighten-2">{{ codeKindLabel }}</span>
            </div>
            <div v-if="codeTab === 'audit'" class="preview-audit">
              <div
                v-for="item in bundle.audit"
                :key="item.id"
                class="preview-audit-row"
              >
                <v-icon small :color="item.ok ? 'success' : 'error'" class="mr-2">
                  {{ item.ok ? 'check_circle' : 'cancel' }}
                </v-icon>
                {{ $t(item.labelKey) }}
              </div>
            </div>
            <pre v-else class="preview-code"><code><span
              v-for="(line, index) in visibleCodeLines"
              :key="index"
              class="preview-code-line"
              :class="{ 'preview-code-line--hit': isSearchHit(line) }"
            ><span class="preview-gutter">{{ index + 1 }}</span>{{ line }}
</span></code></pre>
          </div>
        </div>

        <footer class="preview-footer">
          <div class="d-flex align-center caption">
            <span class="preview-live-dot mr-2" />
            {{ $t('article.previewDialog.microdataLive') }}
          </div>
          <v-btn depressed class="text-none" @click="preview = false">
            {{ $t('article.previewDialog.close') }}
          </v-btn>
        </footer>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
    import Vue from "vue";
    import {sanitizeArticleHtml} from '@/utils/sanitizeHtml';
    import {Article} from "@/model/Article";
    import {Image} from "@/model/Scrapper";
    import {buildPreviewHtml, countUtf8Bytes, PreviewHtmlBundle} from "@/service/articlePreviewHtml";
    import {previewImages as filterPreviewImages} from "@/utils/articleChrome";
    import {stripTagsAndEntities} from "@/utils/string";
    import {copyToClipboard, download} from "@/service/export";
    import {EventBus} from "@/utils/EventBus";
    import {Toast} from "@/model/Toast";
    import {captureException} from "@/plugins/Sentry";
    import EventsListenerMixin from "@/mixins/EventsListenerMixin";

    type ViewMode = 'reader' | 'split' | 'code';
    type DeviceMode = 'desktop' | 'mobile' | 'reading';
    type CodeTab = 'full' | 'schema' | 'cms' | 'audit';

    const FONT_SCALES = [0.9, 1, 1.15];

    let schemaPreviewLock = false;

    export default Vue.extend({
        name: "ArticlePreview",
        mixins: [EventsListenerMixin],
        props: {
            block: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            preview: false,
            expanded: false,
            viewMode: 'reader' as ViewMode,
            deviceMode: 'desktop' as DeviceMode,
            fontFamily: 'sans' as 'sans' | 'serif',
            fontScaleIndex: 1,
            codeTab: 'full' as CodeTab,
            pendingCodeTab: null as CodeTab | null,
            codeSearch: '',
            /** Bumped after editor debounce flush so preview HTML tracks live CKEditor state. */
            previewContentGeneration: 0,
            viewModes: [
                {id: 'reader' as ViewMode, icon: 'visibility', label: 'article.previewDialog.reader'},
                {id: 'split' as ViewMode, icon: 'view_column', label: 'article.previewDialog.split'},
                {id: 'code' as ViewMode, icon: 'code', label: 'article.previewDialog.htmlMicrodata'},
            ],
            devices: [
                {id: 'desktop' as DeviceMode, icon: 'desktop_windows', label: 'article.previewDialog.desktop'},
                {id: 'mobile' as DeviceMode, icon: 'smartphone', label: 'article.previewDialog.mobile'},
                {id: 'reading' as DeviceMode, icon: 'chrome_reader_mode', label: 'article.previewDialog.reading'},
            ],
            codeTabs: [
                {id: 'full' as CodeTab, icon: 'web', label: 'article.previewDialog.tabs.full'},
                {id: 'schema' as CodeTab, icon: 'auto_awesome', label: 'article.previewDialog.tabs.schema'},
                {id: 'cms' as CodeTab, icon: 'code', label: 'article.previewDialog.tabs.cms'},
                {id: 'audit' as CodeTab, icon: 'fact_check', label: 'article.previewDialog.tabs.audit'},
            ],
        }),
        computed: {
            events(): Record<string, () => void> {
                return {
                    'article-preview-schema': this.openSchemaPreview,
                };
            },
            article(): Article {
                return this.$store.getters.article;
            },
            previewImages(): Image[] {
                return filterPreviewImages(this.$store.getters.article.images);
            },
            dialogClass(): string {
                return 'article-preview-dialog' + (this.expanded ? ' article-preview-dialog--expanded' : '');
            },
            fontScale(): number {
                return FONT_SCALES[this.fontScaleIndex] || 1;
            },
            bundle(): PreviewHtmlBundle {
                void this.previewContentGeneration;
                return buildPreviewHtml(this.article, this.$store.getters.articleWordCount.getSum());
            },
            readMinutes(): number {
                return Math.max(1, Math.round((this.bundle.wordCount || 1) / 200));
            },
            sanitizedBody(): string {
                return sanitizeArticleHtml(this.bundle.readerHtml);
            },
            currentCode(): string {
                if (this.codeTab === 'schema') return this.bundle.schemaJsonLd;
                if (this.codeTab === 'cms') return this.bundle.bodyHtml;
                return this.bundle.fullHtml;
            },
            codeLines(): string[] {
                return this.currentCode.split('\n');
            },
            visibleCodeLines(): string[] {
                const query = this.codeSearch.trim().toLowerCase();
                if (!query) return this.codeLines;
                return this.codeLines.filter(line => line.toLowerCase().includes(query));
            },
            codeBytes(): number {
                return countUtf8Bytes(this.currentCode);
            },
            codeKindLabel(): string {
                if (this.codeTab === 'schema') return String(this.$t('article.previewDialog.kind.schema'));
                if (this.codeTab === 'cms') return String(this.$t('article.previewDialog.kind.cms'));
                return String(this.$t('article.previewDialog.kind.full'));
            },
        },
        watch: {
            preview(open: boolean) {
                if (open) {
                    EventBus.$emit('debounce-flush');
                    this.$nextTick(() => {
                        this.previewContentGeneration += 1;
                    });
                    if (this.pendingCodeTab) {
                        this.viewMode = 'code';
                        this.codeTab = this.pendingCodeTab;
                        this.pendingCodeTab = null;
                    } else {
                        this.viewMode = 'reader';
                    }
                    this.expanded = false;
                    this.codeSearch = '';
                }
            }
        },
        methods: {
            openSchemaPreview(): void {
                if (schemaPreviewLock) {
                    return;
                }
                schemaPreviewLock = true;
                this.pendingCodeTab = 'schema';
                if (this.preview) {
                    this.viewMode = 'code';
                    this.codeTab = 'schema';
                    this.pendingCodeTab = null;
                } else {
                    this.preview = true;
                }
                this.$nextTick(() => {
                    schemaPreviewLock = false;
                });
            },
            plainAlt(value: string | undefined): string {
                return stripTagsAndEntities(value || '');
            },
            bumpFont(delta: number): void {
                this.fontScaleIndex = Math.min(FONT_SCALES.length - 1, Math.max(0, this.fontScaleIndex + delta));
            },
            scrollToHeading(id: string): void {
                const root = this.$refs.readerScroll as HTMLElement;
                const target = root && root.querySelector('#' + id);
                if (target) {
                    target.scrollIntoView({behavior: 'smooth', block: 'start'});
                }
            },
            isSearchHit(line: string): boolean {
                const query = this.codeSearch.trim().toLowerCase();
                return !!query && line.toLowerCase().includes(query);
            },
            copiedToast(): void {
                this.$store.dispatch('toasts/add', {
                    type: 'success',
                    title: 'article.copiedToClipboard',
                } as Toast);
            },
            copyText(value: string): void {
                copyToClipboard(value, value)
                    .then(() => this.copiedToast())
                    .catch(error => {
                        captureException(error);
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'article.exportError.clipboard.title',
                        } as Toast);
                    });
            },
            copyFullHtml(): void {
                this.copyText(this.bundle.fullHtml);
            },
            copyCurrentCode(): void {
                this.copyText(this.currentCode);
            },
            copyMarkdown(): void {
                this.copyText(this.bundle.markdown);
            },
            copySchemaForValidator(): void {
                this.copyText(this.bundle.schemaJsonLd);
            },
            downloadCurrent(): void {
                const slug = (this.bundle.title || 'article').slice(0, 60);
                if (this.codeTab === 'schema') {
                    download(this.bundle.schemaJsonLd, 'application/ld+json', slug + '.json');
                    return;
                }
                if (this.codeTab === 'cms') {
                    download(this.bundle.bodyHtml, 'text/html', slug + '-body.html');
                    return;
                }
                download(this.bundle.fullHtml, 'text/html', slug + '.html');
            },
            printPreview(): void {
                const title = String(this.bundle.title || '').replace(/</g, '');
                const html = `<!DOCTYPE html><html><head><title>${title}</title>
<meta charset="utf-8">
<style>body{font-family:Georgia,serif;max-width:720px;margin:32px auto;padding:0 16px;line-height:1.7}img{max-width:100%}</style>
</head><body>${this.sanitizedBody}</body></html>`;
                const frame = document.createElement('iframe');
                frame.setAttribute('aria-hidden', 'true');
                frame.style.position = 'fixed';
                frame.style.right = '0';
                frame.style.bottom = '0';
                frame.style.width = '0';
                frame.style.height = '0';
                frame.style.border = '0';
                const cleanup = () => {
                    if (frame.parentNode) {
                        frame.parentNode.removeChild(frame);
                    }
                };
                frame.onload = () => {
                    try {
                        const win = frame.contentWindow;
                        if (!win) {
                            throw new Error('no print window');
                        }
                        win.focus();
                        win.print();
                    } catch (error) {
                        captureException(error);
                        this.$store.dispatch('toasts/add', {
                            type: 'info',
                            title: 'article.previewDialog.printBlocked',
                        } as Toast);
                    }
                    window.setTimeout(cleanup, 1500);
                };
                document.body.appendChild(frame);
                const doc = frame.contentDocument;
                if (!doc) {
                    cleanup();
                    this.$store.dispatch('toasts/add', {
                        type: 'info',
                        title: 'article.previewDialog.printBlocked',
                    } as Toast);
                    return;
                }
                doc.open();
                doc.write(html);
                doc.close();
            },
        }
    });
</script>

<style lang="sass">
.article-preview-dialog
    margin: 16px
    height: 92vh
    max-height: 92vh
    display: flex
    flex-direction: column
    overflow: hidden

    &.article-preview-dialog--expanded,
    &.v-dialog--fullscreen
        margin: 0
        height: 100vh
        max-height: 100vh
        width: 100%
        max-width: 100%

.preview-shell
    height: 100%
    display: flex
    flex-direction: column
    background: var(--sw-reader-shell) !important

.preview-header
    padding: 16px 20px 8px
    background: var(--sw-reader-bar)

.preview-toolbar
    display: flex
    flex-wrap: wrap
    justify-content: space-between
    gap: 8px
    margin: 0 16px 8px
    padding: 10px 12px
    border-radius: var(--sw-radius-md)
    background: var(--sw-reader-bar)
    border: 1px solid var(--sw-reader-border)

.preview-toolbar-right
    gap: 4px

.preview-seg
    border: 0
    background: var(--sw-reader-seg)
    color: var(--sw-reader-seg-text)
    border-radius: var(--sw-radius-pill)
    padding: 6px 12px
    margin: 2px
    font-size: 13px
    font-weight: 600
    cursor: pointer

    &--quiet
        background: transparent

    &--active
        background: var(--sw-reader-seg-on)
        color: var(--sw-reader-seg-on-text)

.preview-body
    flex: 1
    min-height: 0
    display: grid
    gap: 12px
    padding: 0 16px
    overflow: hidden

    &--reader
        grid-template-columns: 1fr

    &--split
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)

    &--code
        grid-template-columns: 1fr

.preview-reader-scroll
    overflow: auto
    min-height: 0
    padding: 8px 4px 24px

.preview-paper
    background: var(--sw-reader-paper)
    border-radius: var(--sw-radius-md)
    margin: 0 auto 24px
    padding: 40px 48px 64px
    box-shadow: var(--sw-shadow-md)
    border: 1px solid var(--sw-reader-border)
    color: var(--sw-reader-text)
    line-height: 1.7
    width: 100%

    &--desktop
        max-width: 820px

    &--desktop.preview-paper--full
        max-width: 1100px

    &--mobile
        max-width: 390px
        padding: 24px 20px 48px

    &--reading
        max-width: 680px
        padding: 48px 56px 72px

    &--serif
        font-family: Georgia, 'Times New Roman', serif

    &--sans
        font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif

    h1
        font-size: 2em
        line-height: 1.2
        font-weight: 800
        margin: 0 0 12px
        color: var(--sw-reader-heading)

    h2, h3, h4
        margin: 1.4em 0 0.5em
        color: var(--sw-reader-heading)

    p, li, blockquote
        margin: 0 0 1em
        max-width: 35em

    a
        color: var(--sw-reader-link)

    a:visited
        color: var(--sw-reader-link-visited)

    blockquote
        border-left: 3px solid var(--sw-reader-quote-line)
        background: var(--sw-reader-quote-bg)
        color: var(--sw-reader-quote-text)
        padding: 12px 16px

    code
        background: var(--sw-reader-code-bg)
        color: var(--sw-reader-code-text)

    th
        background: var(--sw-reader-th)
        color: var(--sw-reader-th-text)

    tr:nth-child(even) td
        background: var(--sw-reader-zebra)

    img
        max-width: 100%
        border-radius: 12px

.preview-meta
    display: flex
    flex-wrap: wrap
    gap: 8px
    color: var(--sw-reader-muted)
    font-size: 0.85em
    padding-bottom: 16px
    margin-bottom: 16px
    border-bottom: 1px solid var(--sw-reader-border)

.preview-toc
    background: var(--sw-reader-toc)
    border-radius: var(--sw-radius-md)
    padding: 16px 20px
    margin: 0 0 28px

    ol
        margin: 8px 0 0
        padding-left: 0
        list-style: none

    a
        color: var(--sw-reader-heading)
        text-decoration: none

.preview-toc-title
    font-size: 0.75em
    letter-spacing: 0.08em
    font-weight: 700
    color: var(--sw-reader-muted)

.preview-toc-num
    color: var(--sw-reader-toc-num)
    font-weight: 700
    margin-right: 6px

.preview-images
    margin-top: 24px

    figcaption
        color: var(--sw-reader-muted)
        font-size: 0.85em
        margin-top: 6px

.preview-code-pane
    min-height: 0
    display: flex
    flex-direction: column
    border-radius: 16px
    background: #0f172a
    color: #e2e8f0
    overflow: hidden

.preview-code-tabs
    display: flex
    flex-wrap: wrap
    gap: 4px
    padding: 10px 12px 0

    .preview-seg
        background: #1e293b
        color: #cbd5e1

        &--active
            background: var(--sw-reader-seg-on)
            color: var(--sw-reader-seg-on-text)

.preview-search
    max-width: 280px

    .v-input__slot
        background: #1e293b !important
        font-size: 12px

.preview-code
    flex: 1
    min-height: 0
    overflow: auto
    margin: 0
    padding: 8px 0 24px
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace
    font-size: 12px
    line-height: 1.55
    color: #e2e8f0
    white-space: pre-wrap
    word-break: break-word

.preview-code-line
    display: block
    padding: 0 16px

    &--hit
        background: rgba(99, 102, 241, 0.28)

.preview-gutter
    display: inline-block
    width: 36px
    margin-right: 12px
    color: #64748b
    text-align: right
    user-select: none

.preview-audit
    padding: 8px 20px 24px
    overflow: auto

.preview-audit-row
    display: flex
    align-items: center
    padding: 8px 0
    border-bottom: 1px solid #1e293b

.preview-footer
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 10px 20px
    background: var(--sw-reader-bar)
    border-top: 1px solid var(--sw-reader-border)

.preview-live-dot
    width: 8px
    height: 8px
    border-radius: 50%
    background: var(--v-accent-base)
    display: inline-block

@media (max-width: 959px)
    .preview-body--split
        grid-template-columns: 1fr

    .preview-paper
        padding: 24px 20px 40px
</style>
